import i18n from '../../i18n/config';
// arsenal-tpms/frontend/src/components/sensors/SensorForm.jsx (ACTUALIZADO)
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Divider,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  IconButton
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

const flattenVehicles = (vehicles = [], depth = 0) => {
  if (!Array.isArray(vehicles)) return [];

  return vehicles.flatMap((vehicle) => {
    const current = {
      ...vehicle,
      nestingDepth: depth,
    };
    const children = flattenVehicles(vehicle.child_vehicles || [], depth + 1);
    return [current, ...children];
  });
};

const SensorForm = ({
  open,
  onClose,
  onSubmit,
  sensor = null,
  loading = false,
  error = null,
  vehicles = []
}) => {
  const isEditing = !!sensor;
  const { t } = useTranslation();

  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [sensorEntries, setSensorEntries] = useState([{ sensor_id: '', position: '' }]);
  const [batchError, setBatchError] = useState(null);
  const availableVehicles = flattenVehicles(vehicles);

  const validationSchema = Yup.object({
    sensor_id: Yup.string()
      .required(t('sensorForm.sensorIdRequired'))
      .min(3, t('sensorForm.sensorIdMinLength'))
      .max(50, t('sensorForm.sensorIdMaxLength')),
  });

  const formik = useFormik({
    initialValues: {
      sensor_id: sensor?.sensor_id || '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const result = await onSubmit(values);
      if (result && !result.error) {
        handleClose();
      }
    },
  });

  const handleEntryChange = (index, field, value) => {
    setBatchError(null);
    setSensorEntries((previous) => previous.map((entry, entryIndex) => (
      entryIndex === index ? { ...entry, [field]: value } : entry
    )));
  };

  const handleAddEntry = () => {
    setBatchError(null);
    setSensorEntries((previous) => [...previous, { sensor_id: '', position: '' }]);
  };

  const handleRemoveEntry = (index) => {
    setBatchError(null);
    setSensorEntries((previous) => {
      if (previous.length === 1) {
        return previous;
      }
      return previous.filter((_, entryIndex) => entryIndex !== index);
    });
  };

  const handleClose = () => {
    formik.resetForm();
    setSelectedVehicle('');
    setSensorEntries([{ sensor_id: '', position: '' }]);
    setBatchError(null);
    onClose();
  };

  const getAvailablePositions = (entryIndex) => {
    if (!selectedVehicle) return [];

    const vehicle = availableVehicles.find(v => v.id === selectedVehicle);
    if (!vehicle) return [];

    const occupiedPositions = vehicle.vehicle_sensors?.map(vs => vs.position) || [];
    const selectedPositions = sensorEntries
      .filter((_, index) => index !== entryIndex)
      .map((entry) => Number(entry.position))
      .filter(Boolean);
    const allPositions = Array.from({ length: vehicle.positions_count }, (_, i) => i + 1);

    return allPositions.filter((position) => {
      const currentPosition = Number(sensorEntries[entryIndex]?.position);
      return (
        !occupiedPositions.includes(position) &&
        (!selectedPositions.includes(position) || currentPosition === position)
      );
    });
  };

  const handleBatchSubmit = async (event) => {
    event.preventDefault();
    setBatchError(null);

    const normalizedEntries = sensorEntries.map((entry) => ({
      sensor_id: entry.sensor_id.trim(),
      position: entry.position ? Number(entry.position) : null,
    }));
    const sensorsToCreate = normalizedEntries.filter((entry) => entry.sensor_id);

    if (!sensorsToCreate.length) {
      setBatchError(t('hardcoded.components_sensors_sensorform.agrega_al_menos_un_sensor'));
      return;
    }

    if (normalizedEntries.some((entry) => !entry.sensor_id && entry.position)) {
      setBatchError(t('hardcoded.components_sensors_sensorform.no_puedes_seleccionar_una_posicion'));
      return;
    }

    const normalizedIds = sensorsToCreate.map((entry) => entry.sensor_id.toUpperCase());
    if (new Set(normalizedIds).size !== normalizedIds.length) {
      setBatchError(t('hardcoded.components_sensors_sensorform.no_puedes_registrar_ids_de'));
      return;
    }

    const selectedPositions = sensorsToCreate.map((entry) => entry.position).filter(Boolean);
    if (new Set(selectedPositions).size !== selectedPositions.length) {
      setBatchError(t('hardcoded.components_sensors_sensorform.no_puedes_asignar_dos_sensores'));
      return;
    }

    const result = await onSubmit({
      vehicle_id: selectedVehicle || null,
      sensors: sensorsToCreate,
    });

    if (result && !result.error) {
      handleClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
        'data-tutorial-id': 'sensor-form-dialog',
      }}
    >
      <DialogTitle>
        {isEditing ? t('sensorForm.editTitle') : t('sensorForm.createTitle')}
      </DialogTitle>

      <form onSubmit={isEditing ? formik.handleSubmit : handleBatchSubmit}>
        <DialogContent>
          {(error || batchError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {batchError || error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {isEditing
                ? t('hardcoded.components_sensors_sensorform.actualiza_el_identificador_del_sensor')
                : t('hardcoded.components_sensors_sensorform.registra_uno_o_varios_sensores')}
            </Typography>

            <Divider />

            {isEditing ? (
              <TextField
                fullWidth
                name="sensor_id"
                label={t('sensorForm.sensorId')}
                value={formik.values.sensor_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.sensor_id && Boolean(formik.errors.sensor_id)}
                helperText={formik.touched.sensor_id && formik.errors.sensor_id}
                placeholder={t('sensorForm.sensorIdHelper')}
                inputProps={{
                  'data-tutorial-id': 'sensor-form-id',
                }}
              />
            ) : (
              <>
                {availableVehicles.length > 0 && (
                  <>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {t('hardcoded.components_sensors_sensorform.asignar_a_vehiculo_opcional')}</Typography>

                    <FormControl fullWidth>
                      <InputLabel>{t('vehicles.table.vehicle')}</InputLabel>
                      <Select
                        value={selectedVehicle}
                        onChange={(e) => {
                          setBatchError(null);
                          setSelectedVehicle(e.target.value);
                          setSensorEntries((previous) => previous.map((entry) => ({
                            ...entry,
                            position: '',
                          })));
                        }}
                        label={t('vehicles.table.vehicle')}
                        inputProps={{
                          'data-tutorial-id': 'sensor-form-vehicle',
                        }}
                      >
                        <MenuItem value="">
                          <em>Ninguno</em>
                        </MenuItem>
                        {availableVehicles.map((vehicle) => (
                          <MenuItem key={vehicle.id} value={vehicle.id}>
                            {`${'  '.repeat(vehicle.nestingDepth || 0)}${vehicle.display_name}`}
                            <Chip
                              label={`${vehicle.vehicle_sensors?.length || 0}/${vehicle.positions_count}`}
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}

                <Stack spacing={2}>
                  {sensorEntries.map((entry, index) => {
                    const availablePositions = getAvailablePositions(index);

                    return (
                      <Box
                        key={t('hardcoded.components_sensors_sensorform.sensor-entry-index', { index: index })}
                        sx={{
                          p: 2,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {i18n.t('hardcoded.components_schematics_vehicleticketsdrawer.sensor')}{index + 1}
                          </Typography>
                          {sensorEntries.length > 1 && (
                            <IconButton color="error" onClick={() => handleRemoveEntry(index)}>
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Box>

                        <TextField
                          fullWidth
                          label={t('sensorForm.sensorId')}
                          value={entry.sensor_id}
                          onChange={(event) => handleEntryChange(index, 'sensor_id', event.target.value)}
                          placeholder={t('sensorForm.sensorIdHelper')}
                          inputProps={{
                            'data-tutorial-id': index === 0 ? 'sensor-form-id' : undefined,
                          }}
                        />

                        {selectedVehicle && (
                          <FormControl fullWidth>
                            <InputLabel>{i18n.t('alarms.list.position')}</InputLabel>
                            <Select
                              value={entry.position}
                              onChange={(event) => handleEntryChange(index, 'position', event.target.value)}
                              label={t('alarms.list.position')}
                            >
                              <MenuItem value="">
                                <em>{t('sensors.stats.unassigned')}</em>
                              </MenuItem>
                              {availablePositions.length === 0 ? (
                                <MenuItem disabled>
                                  <em>{t('hardcoded.components_sensors_sensorform.no_hay_posiciones_disponibles')}</em>
                                </MenuItem>
                              ) : (
                                availablePositions.map((position) => (
                                  <MenuItem key={position} value={position}>
                                    {i18n.t('alarms.list.position')}{position}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                        )}
                      </Box>
                    );
                  })}
                </Stack>

                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddEntry}
                >
                  {i18n.t('hardcoded.components_sensors_sensorform.agregar_otro_sensor')}</Button>

                {selectedVehicle && (
                  <Alert severity="info">
                    {t('hardcoded.components_sensors_sensorform.puedes_registrar_varios_sensores_y')}<strong>{availableVehicles.find(v => v.id === selectedVehicle)?.display_name}</strong>.
                  </Alert>
                )}
              </>
            )}

            <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {t('sensorForm.sensorInfo')}
              </Typography>
              {t('sensorForm.sensorInfoDetails', { returnObjects: true }).map((detail, index) => (
                <Typography key={index} variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                  • {detail}
                </Typography>
              ))}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2.5, pt: 1 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            color="inherit"
          >
            {t('sensorForm.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || (isEditing && !formik.isValid)}
            sx={{ minWidth: 100 }}
          >
            {loading ? t('sensorForm.saving') : isEditing ? t('sensorForm.update') : t('sensorForm.register')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SensorForm;